import { Form } from '@ant-design/compatible';
import { Col } from 'antd';
import AlertsContainer from 'common/alerts/containers/alert-container';
import ErrorHandler from 'common/error/components/error-handler';
import ActionButtons from 'common/navigation/components/action-buttons';
import Notes from 'common/notes/notes';
import AdditionalPartners from 'common/partners/components/additional-partners';
import MainPartners from 'common/partners/components/main-partners';
import Spinner from 'common/spinner/components/spinner';
import { DynamicActions } from 'common/toolbar';
import PartsReadonly from 'order/parts/components/parts-readonly';
import TaskListReadonly from 'order/task-list/components/task-list-readonly';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { compose } from 'redux';
import styled from 'styled-components';
import TicketButton from 'tickets/ticket-button';
import { Div, Flex, Info, Row, Title, Icon } from 'ui';
import { mainPartnerTypes, partnerTypes as partnerTypeByName } from 'utils/app-configs';
import { withRouter } from 'utils/withRouter';
import General from './general';

const OrderLink = styled.a`
  color: #0078a1;
  text-decoration: underline;
  &:hover {
    color: #0098cc;
    text-decoration: underline;
  }
`;

const OrdersSubTitle = styled(Title)`
  font-size: 15px;
  font-weight: bold;
  padding-top: 15px;
  padding-bottom: 20px;
  text-transform: uppercase;
`;

const StyledIcon = styled(Icon)`
  color: #0078a1;
  margin-right: ${p => p.small ? '5px' : '0'};
`;

class NotificationCallInfo extends Component {
  static propTypes = {
    id: PropTypes.string,
    isNotification: PropTypes.bool,
    fetchNotification: PropTypes.func,
    fetchCall: PropTypes.func,
    data: PropTypes.object,
    partners: PropTypes.array,
    history: PropTypes.object,
    partnerTypes: PropTypes.array,
    notes: PropTypes.array,
    taskList: PropTypes.object,
    partList: PropTypes.object,
    renderMenu: PropTypes.func,
    error: PropTypes.object,
    actions: PropTypes.array,
    isLoading: PropTypes.bool,
    intl: PropTypes.object,
    parentInfo: PropTypes.object,
  };

  componentDidMount() {
    if (this.props.id && this.props.isNotification) {
      this.props.fetchNotification({ notificationId: this.props.id });
    }
    if (this.props.id && !this.props.isNotification) {
      this.props.fetchCall({ callId: this.props.id });
    }
  }

  handleOpenOrder = (e) => {
    e.preventDefault();
    const { history, data } = this.props;
    if (data?.orderId) {
      const orderid = encodeURIComponent(data.orderId);
      history.push(`/order/${orderid}`, { context: orderid });
    }
  };

  render() {
    const {
      data = {},
      partners,
      partnerTypes,
      notes,
      taskList,
      partList,
      error,
      actions = [],
      renderMenu,
      isLoading,
      intl,
      isNotification,
      parentInfo,
    } = this.props;

    /* eslint-disable max-len */
    const mainPartnersArray = mainPartnerTypes.reduce((a, i) => {
      a.push(i.type.toUpperCase());
      return a;
    }, []);

    const shipTo = partners?.find(
      p => p.partnerFunction?.toUpperCase() === partnerTypeByName.SHIPTO,
    );

    const mainPartners = partners.reduce((all, p) => {
      if (mainPartnersArray.includes(p.partnerFunction.toUpperCase())) {
        // Filter out double ids
        if (!all?.find(par => par.id === p.id)) {
          return [...all, p];
        }
      }

      return all;
    }, []);

    const additionalPartners = partners?.filter(
      p => !mainPartnersArray.includes(p.partnerFunction.toUpperCase()),
    );
    const mainTypes =
      partnerTypes && partnerTypes?.filter(p => mainPartnersArray.includes(p.type.toUpperCase()));
    /* eslint-enable max-len */

    const additionalTypes =
      partnerTypes && partnerTypes?.filter(p => !mainPartnersArray.includes(p.type));

    const availableActions = actions.reduce(
      (actionObject, action) => ({
        ...actionObject,
        [action.id]: actionObject[action.id]
          ? [...actionObject[action.id], action.payload]
          : [action.payload],
      }),
      {},
    );

    return (
      <>
        {renderMenu(
          <ActionButtons>
            <DynamicActions
              reload={() => {
                if (this.props.id && this.props.isNotification) {
                  this.props.fetchNotification({ notificationId: this.props.id });
                }
                if (this.props.id && !this.props.isNotification) {
                  this.props.fetchCall({ callId: this.props.id });
                }
              }}
              showAsQuickAccess={true}
              availableActions={availableActions}
            />
            {isNotification ? <TicketButton id={data.id} /> : null}
          </ActionButtons>,
        )}
        {isLoading && <Spinner title={intl.formatMessage({ id: 'common.LOADING' })} />}
        {!isLoading &&
          <Info>
            <Form layout='vertical'>
              <ErrorHandler type='notification' error={error} />
              <>
                <Flex>
                  <Title>{data.name}</Title>
                </Flex>
                {data?.orderId &&
                  <OrdersSubTitle>
                    <StyledIcon small={true} type='link' />
                    <FormattedMessage
                      id='order.fieldEngineer.DETAILS_NOTIFICATION_ORDER'
                      values={{
                        link: <OrderLink onClick={this.handleOpenOrder}>
                          <FormattedMessage
                            id='order.fieldEngineer.OPEN_ORDER'
                          />
                        </OrderLink>,
                      }}
                    />
                  </OrdersSubTitle>
                }
                <Row type='flex' gutter={20} marginBottom={45}>
                  <Col sm={12} xs={24}>
                    <MainPartners
                      cardsPerLine={2}
                      partners={mainPartners}
                      types={mainTypes}
                      canEdit={false}
                      isLoading={false}
                    />
                  </Col>
                  <Col sm={12} xs={24}>
                    <Div marginBottom={20}>
                      <AlertsContainer maxVisibleAlerts={3} small={true} />
                    </Div>
                    <Notes notes={notes} type='notification' readonly={true} small={true} />
                  </Col>
                </Row>
                <Row marginBottom={45} marginRight={20}>
                  <AdditionalPartners
                    orderType={data.businessIndicator}
                    partners={additionalPartners}
                    types={additionalTypes}
                    isLoading={false}
                    readonly={true}
                  />
                </Row>
                <Row>
                  <Col span={12}>
                    {isNotification && <General data={data} parentInfo={parentInfo} />}
                  </Col>
                </Row>
                <Row>
                  <Col span={24}>
                    <PartsReadonly data={partList} shipTo={shipTo} orderId={data?.id} />
                  </Col>
                </Row>
                <Row>
                  <Col span={24}>
                    {taskList && Object.keys(taskList).length > 0 &&
                      <TaskListReadonly taskList={taskList} />
                    }
                  </Col>
                </Row>
              </>
            </Form>
          </Info>
        }
      </>
    );
  }
}

export default compose(
  withRouter,
  injectIntl,
)(NotificationCallInfo);
